/**
 * Asset loading: two GLB kits + their manifests + the palette variation maps.
 *
 * Parsed scenes are kept as TEMPLATES and never added to the render scene —
 * `getPiece` hands out clones (geometry/material are shared, so clones are
 * cheap). Nature-kit ids are namespaced with the `nature-kit:` prefix to match
 * the generator's decor placements.
 */
import * as THREE from "three";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";
import type { ManifestPiece, PackManifest } from "../shared/types.ts";

export const MARBLE_PACK = "marble-kit";
export const NATURE_PACK = "nature-kit";
/** Every decor pack the client streams (template keys "<slug>:<id>").
 * The marble kit stays special (bare keys, palette machinery). */
export const DECOR_PACKS = [
  NATURE_PACK,
  // M18a theme packs.
  "holiday-kit",
  "graveyard-kit",
  "pirate-kit",
  "survival-kit",
  // M18b void decor.
  "space-kit",
  // Island mass families (block voxel bodies).
  "platformer-kit",
] as const;

export interface Assets {
  /** pieceId → template object (marble-kit bare, decor packs prefixed). */
  templates: Map<string, THREE.Object3D>;
  marbleManifest: PackManifest;
  natureManifest: PackManifest;
  /** Every decor-pack manifest by slug (nature-kit included). */
  manifests: Map<string, PackManifest>;
  /** Ordered ids as they appear in each manifest. */
  marbleIds: string[];
  natureIds: string[];
  /** Clone of a template with shadows enabled, or null for unknown ids. */
  getPiece(id: string): THREE.Object3D | null;
  /** Swap the marble kit's shared colormap to variation a/b/c; an
   * optional pipe color recolors the track-body swatch (per-seed
   * variation — null keeps the kit's stock color). */
  applyPalette(
    index: 0 | 1 | 2,
    pipeColor?: readonly [number, number, number] | null,
  ): void;
  /** Recolor ONE cloned piece's pipe swatch (endless per-island
   * chapters): swaps its shared colormap materials for cached clones
   * whose texture has the pipe texels repainted. Null is a no-op. */
  applyPipeColor(
    root: THREE.Object3D,
    color: readonly [number, number, number] | null,
  ): void;
}

function parseGLB(loader: GLTFLoader, buf: ArrayBuffer): Promise<THREE.Group> {
  return new Promise((resolve, reject) => {
    loader.parse(
      buf,
      "",
      (gltf: { scene: THREE.Group }) => resolve(gltf.scene),
      reject,
    );
  });
}

async function fetchBuffer(url: string): Promise<ArrayBuffer> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`${url}: ${res.status} ${res.statusText}`);
  return await res.arrayBuffer();
}

async function fetchJSON<T>(url: string): Promise<T> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`${url}: ${res.status} ${res.statusText}`);
  return await res.json() as T;
}

function loadTexture(url: string): Promise<THREE.Texture> {
  return new THREE.TextureLoader().loadAsync(url).then((tex: THREE.Texture) => {
    tex.flipY = false;
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.needsUpdate = true;
    return tex;
  });
}

/** Materials in a subtree that carry a colormap, deduplicated. */
function collectMappedMaterials(
  root: THREE.Object3D,
): THREE.MeshStandardMaterial[] {
  const out = new Set<THREE.MeshStandardMaterial>();
  root.traverse((obj: THREE.Object3D) => {
    const mesh = obj as THREE.Mesh;
    if (!mesh.isMesh) return;
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    for (const m of mats) {
      const std = m as THREE.MeshStandardMaterial;
      if (std && std.map) out.add(std);
    }
  });
  return [...out];
}

export async function loadAssets(): Promise<Assets> {
  const loader = new GLTFLoader();

  const [
    marbleBuf,
    marbleManifest,
    texA,
    texB,
    texC,
    ...decorLoads
  ] = await Promise.all([
    fetchBuffer("/assets/marble-kit.glb"),
    fetchJSON<PackManifest>("/assets/marble-kit.manifest.json"),
    loadTexture("/assets/textures/variation-a.png"),
    loadTexture("/assets/textures/variation-b.png"),
    loadTexture("/assets/textures/variation-c.png"),
    ...DECOR_PACKS.flatMap((slug) => [
      fetchBuffer(`/assets/${slug}.glb`),
      fetchJSON<PackManifest>(`/assets/${slug}.manifest.json`),
    ]),
  ] as const) as [
    ArrayBuffer,
    PackManifest,
    THREE.Texture,
    THREE.Texture,
    THREE.Texture,
    ...(ArrayBuffer | PackManifest)[],
  ];

  const manifests = new Map<string, PackManifest>();
  const decorScenes: THREE.Group[] = [];
  const marbleScene = await parseGLB(loader, marbleBuf);
  for (let i = 0; i < DECOR_PACKS.length; i++) {
    const buf = decorLoads[i * 2] as ArrayBuffer;
    const manifest = decorLoads[i * 2 + 1] as PackManifest;
    manifests.set(DECOR_PACKS[i], manifest);
    decorScenes.push(await parseGLB(loader, buf));
  }
  const natureManifest = manifests.get(NATURE_PACK)!;

  const templates = new Map<string, THREE.Object3D>();
  for (const child of marbleScene.children) {
    if (child.name) templates.set(child.name, child);
  }
  for (let i = 0; i < DECOR_PACKS.length; i++) {
    for (const child of decorScenes[i].children) {
      if (child.name) templates.set(`${DECOR_PACKS[i]}:${child.name}`, child);
    }
  }

  // Kenney GLTFs declare baseColorFactor with NO metallicFactor, and the
  // glTF spec default is metallic = 1.0 — GLTFLoader dutifully builds
  // full-metal materials, which render BLACK wherever direct sun doesn't
  // reach (we have no environment map; metals ignore hemisphere diffuse).
  // The kits are authored for flat diffuse — normalize on load.
  for (const scene of [marbleScene, ...decorScenes]) {
    scene.traverse((obj: THREE.Object3D) => {
      const mesh = obj as THREE.Mesh;
      if (!mesh.isMesh) return;
      const mats = Array.isArray(mesh.material)
        ? mesh.material
        : [mesh.material];
      for (const m of mats) {
        const std = m as THREE.MeshStandardMaterial;
        if (std.isMeshStandardMaterial) std.metalness = 0;
      }
    });
  }

  // The marble kit shares one colormap material across all 162 pieces; grab it
  // once so palette swaps are a single `.map` assignment.
  const palettes: THREE.Texture[] = [texA, texB, texC];
  const marbleMaterials = collectMappedMaterials(marbleScene);
  const baseMaps = marbleMaterials.map((m) => m.map);

  // Pipe-body swatch color per variation (measured: straight/curve/slant
  // UVs land on exactly this texel color in each colormap). Per-seed pipe
  // colors repaint every matching texel on a canvas copy — a swatch-level
  // recolor, not a blanket material tint.
  const PIPE_SWATCH: readonly (readonly [number, number, number])[] = [
    [207, 83, 79], // variation-a
    [255, 192, 68], // variation-b
    [134, 139, 161], // variation-c
  ];
  /** Derived pipe-colored colormaps + material clones, one entry per
   * (variation, color) — chapters share entries; LRU-bounded, texture
   * and materials evicted together. */
  const derived = new Map<
    string,
    { tex: THREE.Texture; mats: THREE.MeshStandardMaterial[] | null }
  >();

  function pipeEntry(
    index: 0 | 1 | 2,
    color: readonly [number, number, number],
  ): { tex: THREE.Texture; mats: THREE.MeshStandardMaterial[] | null } {
    const cacheKey = `${index}/${color.join(",")}`;
    const hit = derived.get(cacheKey);
    if (hit) return hit;
    const src = palettes[index].image as HTMLImageElement;
    const canvas = document.createElement("canvas");
    canvas.width = src.width;
    canvas.height = src.height;
    const ctx = canvas.getContext("2d")!;
    ctx.drawImage(src, 0, 0);
    const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const [pr, pg, pb] = PIPE_SWATCH[index];
    const d = img.data;
    for (let i = 0; i < d.length; i += 4) {
      if (d[i] === pr && d[i + 1] === pg && d[i + 2] === pb) {
        d[i] = color[0];
        d[i + 1] = color[1];
        d[i + 2] = color[2];
      }
    }
    ctx.putImageData(img, 0, 0);
    const tex = new THREE.CanvasTexture(canvas);
    tex.flipY = false;
    tex.colorSpace = THREE.SRGBColorSpace;
    // Match the base map's UV transform once, up front — the derived
    // texture is shared by material clones that never pass through
    // applyPalette.
    const base0 = baseMaps[0];
    if (base0) {
      tex.wrapS = base0.wrapS;
      tex.wrapT = base0.wrapT;
      tex.offset.copy(base0.offset);
      tex.repeat.copy(base0.repeat);
      tex.center.copy(base0.center);
      tex.rotation = base0.rotation;
    }
    // Bound the cache (chapters accumulate over an endless journey; the
    // live render window only ever needs a handful).
    if (derived.size >= 16) {
      const oldest = derived.keys().next().value;
      if (oldest !== undefined) {
        const entry = derived.get(oldest);
        entry?.tex.dispose();
        for (const m of entry?.mats ?? []) m.dispose();
        derived.delete(oldest);
      }
    }
    const made = { tex, mats: null };
    derived.set(cacheKey, made);
    return made;
  }

  let currentIndex: 0 | 1 | 2 = 0;

  function applyPipeColor(
    root: THREE.Object3D,
    color: readonly [number, number, number] | null,
  ): void {
    if (!color) return;
    const entry = pipeEntry(currentIndex, color);
    if (!entry.mats) {
      entry.mats = marbleMaterials.map((m) => {
        const clone = m.clone();
        clone.map = entry.tex;
        clone.needsUpdate = true;
        return clone;
      });
    }
    root.traverse((obj: THREE.Object3D) => {
      const mesh = obj as THREE.Mesh;
      if (!mesh.isMesh) return;
      const swap = (m: THREE.Material): THREE.Material => {
        const at = marbleMaterials.indexOf(m as THREE.MeshStandardMaterial);
        return at >= 0 ? entry.mats![at] : m;
      };
      mesh.material = Array.isArray(mesh.material)
        ? mesh.material.map(swap)
        : swap(mesh.material);
    });
  }

  function applyPalette(
    index: 0 | 1 | 2,
    pipeColor: readonly [number, number, number] | null = null,
  ): void {
    currentIndex = index;
    const tex = pipeColor ? pipeEntry(index, pipeColor).tex : palettes[index];
    if (!tex) return;
    marbleMaterials.forEach((mat, i) => {
      const base = baseMaps[i];
      // Preserve the source map's UV transform (the kit uses
      // KHR_texture_transform) — the variations share its UV layout.
      if (base) {
        tex.wrapS = base.wrapS;
        tex.wrapT = base.wrapT;
        tex.offset.copy(base.offset);
        tex.repeat.copy(base.repeat);
        tex.center.copy(base.center);
        tex.rotation = base.rotation;
      }
      mat.map = tex;
      mat.needsUpdate = true;
    });
  }

  function getPiece(id: string): THREE.Object3D | null {
    const tpl = templates.get(id);
    if (!tpl) return null;
    const obj = tpl.clone(true);
    obj.traverse((child: THREE.Object3D) => {
      const mesh = child as THREE.Mesh;
      if (mesh.isMesh) {
        mesh.castShadow = true;
        mesh.receiveShadow = true;
      }
    });
    return obj;
  }

  return {
    templates,
    marbleManifest,
    natureManifest,
    manifests,
    marbleIds: marbleManifest.pieces.map((p: ManifestPiece) => p.id),
    natureIds: natureManifest.pieces.map((p: ManifestPiece) => p.id),
    getPiece,
    applyPalette,
    applyPipeColor,
  };
}
